import { Search } from '@/components/search'
import { MatchItemLoading } from '@/components/skeleton'
import { matchDateOptions, matchTypeOptions } from '@/constants/DropdownOptionsConstants'
import { ROUTE_NEW_MATCH } from '@/constants/Routes'
import { useAppSelector } from '@/hooks/useRedux'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { EventFormat, Match } from '@/types'
import moment from 'moment'
import React, { Dispatch, SetStateAction } from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { ButtonPrimary } from '../button'
import { Calendar } from '../icons'
import { Select } from '../input'
import GoogleAd from '../sidebar/GoogleAd'
import { MatchesProvider } from '../match/MatchesContextProvider'
import { MatchItem } from '../match/Match'

type Props = {
  isLoading: boolean
  matches: Match[]
  setMatches: Dispatch<SetStateAction<Match[]>>
  playerId: number
  eventFormat: EventFormat | null
  setEventFormat: Dispatch<SetStateAction<EventFormat | null>>
  sortParameter: 'MATCH_DATE' | 'MODIFIED_DATE' | 'CREATED_DATE'
  setSortParameter: Dispatch<SetStateAction<'MATCH_DATE' | 'MODIFIED_DATE' | 'CREATED_DATE'>>
  setOffset: Dispatch<SetStateAction<number>>
  startDate: Date | null
  endDate: Date | null
  setStartDate: Dispatch<SetStateAction<Date | null>>
  setEndDate: Dispatch<SetStateAction<Date | null>>
  searchText: string
  setSearchText: Dispatch<SetStateAction<string>>
  isEmpty?: boolean
}

export const Matches = ({
  isLoading,
  matches,
  playerId,
  eventFormat,
  setEventFormat,
  sortParameter,
  setSortParameter,
  setMatches,
  setOffset,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  searchText,
  setSearchText,
  isEmpty,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAppSelector(userSelector)

  const isMobile = useMediaQuery('(max-width: 767px)')
  const { matchHistoryAdRateMobile, initialMatchesSkippedBeforeFirstAd, isGoogleAdsRollout } =
    useAppSelector(appSelector)

  const navigateNewMatch = () => navigate(ROUTE_NEW_MATCH)

  const onChangeDateRange = (dates: any) => {
    const [start, end] = dates
    setStartDate(start)
    setEndDate(end)
  }

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  return (
    <div className="w-full min-w-0 md:w-2/3 w- flex flex-col gap-y-4 md:gap-y-6">
      <div>
        <h2 className="text-2xl text-white font-bold mb-2">{t('common.matches')}</h2>

        {!isEmpty && (
          <div className="flex flex-col gap-y-2">
            <div className="flex gap-x-2">
              <Select
                placeholder={t('common.select')}
                value={matchTypeOptions.find((option) => option.value === eventFormat)}
                containerClassName="!w-28 !rounded-md"
                buttonClassName="text-white font-semibold rounded-lg border-none bg-white bg-opacity-20 !py-[10px] !pl-4 !pr-2"
                options={matchTypeOptions}
                onChange={setEventFormat}
              />
              <Select
                placeholder={t('common.select')}
                value={matchDateOptions.find((option) => option.value === sortParameter)}
                containerClassName="!w-40 !rounded-md"
                buttonClassName="text-white font-semibold rounded-lg border-none bg-white bg-opacity-20 !py-[10px] !pl-4 !pr-2"
                options={matchDateOptions}
                onChange={setSortParameter}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Calendar
                  className={`absolute top-1/2 -translate-y-1/2 left-4 z-10 pointer-events-none text-navy-900 w-5 h-5`}
                />
                <ReactDatePicker
                  popperClassName="z-20"
                  popperPlacement="top-start"
                  className="px-4 pl-12 py-2 rounded-lg text-sm font-semibold bg-white text-navy-900 focus:outline-none max-w-[256px]"
                  selectsRange
                  selected={startDate}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={onChangeDateRange}
                />
              </div>
              <Search
                searchText={searchText}
                onChangeInput={onChangeInput}
                className="py-2 px-4 w-52 gap-x-2 !rounded-lg"
                placeholder={t('common.eventName')}
              />
            </div>
          </div>
        )}
      </div>

      <MatchesProvider matches={matches} setMatches={setMatches} setOffset={setOffset}>
        {matches?.map((item, index) => {
          const date = moment(item.created)
          const isShowMonth = date.format('MMMM') !== moment(matches[index - 1]?.created).format('MMMM')
          const shouldShowAd =
            isMobile &&
            isGoogleAdsRollout &&
            (index + initialMatchesSkippedBeforeFirstAd) % matchHistoryAdRateMobile === 0

          const shouldShowMonth = !index || isShowMonth

          return (
            <React.Fragment key={item.id}>
              {shouldShowAd && <GoogleAd format="fluid" layoutKey="-6t+ed+2i-1n-4w" slot="5011874905" />}
              {shouldShowMonth && <span className="text-white font-semibold">{date.format('MMMM')}</span>}
              <MatchItem match={item} playerId={playerId} />
            </React.Fragment>
          )
        })}
      </MatchesProvider>

      {!isLoading && !isEmpty && !matches.length && (
        <div className="flex flex-col items-center justify-center px-10">
          <h3 className="text-xl text-white font-bold text-center">{t('common.noMoreResults')}</h3>
        </div>
      )}

      {!isLoading && isEmpty && (
        <div className="flex flex-col items-center justify-center px-10">
          <h3 className="text-xl text-white font-bold text-center">{t('performance.matchesEmpty')}</h3>
          <p className="mt-4 text-center text-xs text-white">{t('performance.matchesEmptyDes')}</p>
          {user?.id === playerId && (
            <ButtonPrimary
              title={t('common.createAMatch')}
              className="!px-4 !py-[10px] mt-2 text-xs"
              onClick={navigateNewMatch}
            />
          )}
        </div>
      )}

      {isLoading && (
        <>
          <MatchItemLoading />
          <MatchItemLoading />
        </>
      )}
    </div>
  )
}
